import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ProsemirrorModule } from '@components/misc/prosemirror/prosemirror.module';
import { SharedModule } from '@components/shared/shared.module';
import { Participant } from '@models/Sessions/Participant';
import { Session } from '@models/Sessions/Session';
import { Team } from '@models/Sessions/Team';
import { NgbModal, NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { BusService } from '@services/bus.service';
import { ConfigService } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import _ from 'lodash';
import { MessageService } from 'primeng/api';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TabsModule } from 'primeng/tabs';
import { TextareaModule } from 'primeng/textarea';

import { ExportWizardModalComponent } from './export-wizard/modal.component';
import { ImportWizardModalComponent } from './import-wizard/modal.component';

@Component({
  selector: 'app-control-center',
  standalone: true,
  imports: [
    CommonModule,
    SharedModule,
    ProsemirrorModule,
    FormsModule,
    NgbModule,
    InputTextModule,
    SelectModule,
    TextareaModule,
    CheckboxModule,
    TabsModule,
  ],
  templateUrl: './control-center.component.html',
  styleUrls: ['./control-center.component.scss'],
})
export class SessionModulesControlCenterComponent implements OnInit, OnDestroy {
  @Input() session: Session;

  settingsChanged = false;
  settingsSaved = false;
  activeUpload = false;

  participant: Participant = {
    email: '',
    role: 2,
    votes_for: [],
    team: {},
  } as any;

  team: Team = {
    name: '',
  } as any;

  selectedParticipant: Participant;
  selectedTeam: Team;

  constructor(
    private bus: BusService,
    private events: EventsService,
    public appConfig: ConfigService,
    private sessionRequest: SessionsRequestService,
    private modalService: NgbModal,
    private messageService: MessageService,
  ) {}

  get teamDict() {
    return _.keyBy(this.session.config.teams, '_id');
  }

  saveConfig() {
    this.bus.publish(
      this.events.requested.action.session.updateConfig,
      this.session,
    );
    this.settingsChanged = false;
    this.settingsSaved = true;
  }

  addParticipant() {
    _.forEach(this.session.config.teams, team => {
      if (team.selectedForNewParticipant) {
        this.participant.votes_for.push(team._id);
        team.selectedForNewParticipant = false;
      }
    });

    this.bus.publish(
      this.events.requested.action.session.participants.add,
      this.participant,
      this.session,
    );
    this.participant = {
      email: '',
      role: 2,
      team: {},
      votes_for: [],
    } as any;
  }

  deleteParticipant(participant: Participant) {
    if (
      confirm(
        'Are you sure you want to delete ' +
          participant.user.name +
          ' from the session?',
      )
    ) {
      this.bus.publish(
        this.events.requested.action.session.participants.delete,
        participant,
        this.session,
      );
    }
  }

  selectParticipant(participant: Participant) {
    this.selectedParticipant = participant;
    _.forEach(this.session.config.teams, team => {
      team.selected =
        this.selectedParticipant.votes_for.indexOf(team._id) !== -1;
    });
  }

  updateParticipant() {
    this.selectedParticipant.votes_for = _.map(
      _.filter(this.session.config.teams, 'selected'),
      '_id',
    );
    this.bus.publish(
      this.events.requested.action.session.participants.update,
      this.selectedParticipant,
      this.session,
    );
  }

  addTeam() {
    this.bus.publish(
      this.events.requested.action.session.teams.add,
      this.team,
      this.session,
    );
    this.team = {
      name: '',
    } as any;
  }

  selectTeam(team: Team) {
    this.selectedTeam = team;
  }

  // Events

  uploadStart() {
    this.activeUpload = true;
  }

  uploadComplete(key) {
    this.activeUpload = false;
    this.session.config.thumbnail_id = key;
    this.settingsChanged = true;
  }

  logoUploadComplete(key) {
    this.activeUpload = false;
    this.session.config.organizer.logo_id = key;
    this.settingsChanged = true;
  }

  // Modals

  openImportWizardModal() {
    const modalRef = this.modalService.open(ImportWizardModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
    modalRef.componentInstance.session = this.session;
  }

  openExportWizardModal() {
    const modalRef = this.modalService.open(ExportWizardModalComponent, {
      size: 'lg',
      windowClass: 'big-modal',
    });
    modalRef.componentInstance.session = this.session;
  }

  ngOnInit() {
    this.subscribe();
    this.selectParticipant(_.values(this.session.config.participants)[0]);
    this.selectTeam(_.values(this.session.config.teams)[0]);

    if (!this.session.config.page_size) {
      this.session.config.page_size = 16;
    }

    if (this.session.config.suggest_tags !== false) {
      this.session.config.suggest_tags = true;
    }

    if (!this.session.config.extra_idea_fields) {
      this.session.config.extra_idea_fields = {
        for_ws: false,
        innovation_sweet_spot_medical: false,
        implementation_steps_medical: false,
      };
    }

    if (!this.session.config.people) {
      this.session.config.people = { active: true };
    }

    if (!this.session.config.pdf_exports) {
      this.session.config.pdf_exports = { active: true };
    }

    if (!this.session.config.matrix.role_view) {
      this.session.config.matrix.role_view = {
        moderators: true,
        participants: true,
        externals: true,
      };
    }
  }

  ngOnDestroy() {
    this.unsubscribe();
  }

  get moderatorsCount() {
    return this.session.config.participants.filter(
      p =>
        p.user &&
        p.user.registered &&
        p.user.email !== 'vgamper@gmail.com' &&
        p.role <= this.appConfig.roles.SESSION_MODERATOR,
    ).length;
  }

  get participantsCount() {
    return this.session.config.participants.filter(
      p =>
        p.user &&
        p.user.registered &&
        p.role === this.appConfig.roles.SESSION_PARTICIPANT,
    ).length;
  }

  get externalsCount() {
    return this.session.config.participants.filter(
      p =>
        p.user &&
        p.user.registered &&
        p.role === this.appConfig.roles.SESSION_EXTERNAL_EVALUATOR,
    ).length;
  }

  participantName(participant: Participant) {
    if (!participant.user) {
      return 'DELETED';
    } else {
      let name = '';
      if (participant.user.registered) {
        name = participant.user.first_name
          ? `${participant.user.first_name} ${participant.user.name}`
          : participant.user.name;
      } else {
        name = `UNREGISTERED`;
      }

      return `${name} (${this.teamDict[participant.team as any].name}) ${participant.user.email}`;
    }
  }

  private handleUpdateConfigFailure() {
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to update Topic.',
    });
  }

  private subscribe() {
    this.bus.subscribe(
      this.events.received.action.session.updateConfig.failure,
      this.handleUpdateConfigFailure,
      this,
    );
  }

  private unsubscribe() {
    this.bus.unsubscribe(
      this.events.requested.action.session.updateConfig,
      this.handleUpdateConfigFailure,
    );
  }
}
